// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { EveryMessage } from "@sendbird/uikit-react/types/types";

import { ChatMessageItem } from "@/components/shared/chat-message-item";
import type {
  ChatConversationMember,
  ChatMessage,
} from "@/components/shared/chat-message-types";
import { getSendbirdMessageSenderDisplayName } from "@/lib/redacted-display";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { isMessageHidden } from "@/lib/utils/parse-message-data";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import EmojiReactions from "@sendbird/uikit-react/ui/EmojiReactions";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { ReplyIcon } from "lucide-react";
import { useCallback, useMemo } from "react";

interface MessageItemProps {
  message: EveryMessage;
}

export function MessageItem({ message }: MessageItemProps) {
  const store = useSendbirdStateContext();
  const userId = store.config.userId;
  const channelStore = useChannelContext();
  const channel = channelStore?.currentGroupChannel;
  const isCurrentUser = message?.sender?.userId === userId;

  const senderDisplayName = useMemo(
    () => getSendbirdSenderDisplayName(message),
    [message],
  );

  const { url: profileUrl } = useSignedMedia(message?.sender?.profileUrl);
  const conversationMembers = normalizeConversationMembers(channel?.members);
  const chatMessage = useMemo(
    () => normalizeSendbirdMessage(message, senderDisplayName),
    [message, senderDisplayName],
  );

  const isRead =
    message.sendingStatus === "succeeded" &&
    channel?.getUnreadMemberCount?.(message) === 0;
  const isHidden = isMessageHidden((message as { data?: string }).data);

  const handleQuoteMessage = useCallback(() => {
    channelStore?.setQuoteMessage?.(message);
  }, [channelStore, message]);

  return (
    <ChatMessageItem
      message={chatMessage}
      layout={{
        alignment: isCurrentUser ? "right" : "left",
        messageStyle: isCurrentUser ? "outgoing" : "incoming",
      }}
      sender={{
        label: isCurrentUser ? "You" : senderDisplayName,
        profileUrl: profileUrl,
      }}
      slots={{
        bubbleAccessories:
          !message.parentMessage && !isHidden ? (
            <button
              onClick={handleQuoteMessage}
              className="flex min-h-10 min-w-10 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200"
            >
              <ReplyIcon />
            </button>
          ) : null,
        metaExtra:
          isCurrentUser && isRead ? (
            <>
              <div className="h-1 w-1 rounded-full bg-gray-600" />
              <span className="text-xs uppercase text-gray-500">Read</span>
            </>
          ) : null,
        reactions: message.sender ? (
          <EmojiReactions
            userId={userId}
            message={message}
            channel={channel}
            emojiContainer={store.emojiManager.emojiContainer}
            memberNicknamesMap={channelStore.nicknamesMap}
            toggleReaction={channelStore.toggleReaction}
          />
        ) : null,
      }}
      context={{
        surface: "mentor",
        conversation: {
          members: conversationMembers,
        },
      }}
      behavior={{
        onParentMessageClick: (parentMessage) => {
          document
            .querySelector(`[data-sb-message-id="${parentMessage.id}"]`)
            ?.scrollIntoView({ behavior: "smooth" });
        },
      }}
    />
  );
}

function getSendbirdSenderDisplayName(message: EveryMessage): string {
  const senderMetaRole = (
    message?.sender as { metaData?: { role?: string } } | undefined
  )?.metaData?.role;

  if (!message?.sender) {
    return "";
  }

  return (
    getSendbirdMessageSenderDisplayName({
      user_id: message.sender.userId,
      nickname: message.sender.nickname,
      metadata: { role: senderMetaRole },
    }) ?? message.sender.nickname
  );
}

function normalizeSendbirdMessage(
  message: EveryMessage,
  senderDisplayName: string,
): ChatMessage {
  return {
    id: message.messageId,
    kind: message.isFileMessage()
      ? "file"
      : message.isAdminMessage()
        ? "admin"
        : "message",
    text: message.message,
    data: (message as { data?: string }).data,
    customType: message.customType,
    createdAt: message.createdAt,
    sender: message.sender
      ? {
          id: message.sender.userId,
          nickname: message.sender.nickname,
          displayName: senderDisplayName,
          profileUrl: message.sender.profileUrl,
        }
      : null,
    attachment: message.isFileMessage()
      ? {
          url: (message as { url?: string }).url,
          mimeType: (message as { type?: string }).type,
        }
      : null,
    parentMessage: message.parentMessage
      ? normalizeSendbirdMessage(
          message.parentMessage as EveryMessage,
          getSendbirdSenderDisplayName(message.parentMessage as EveryMessage),
        )
      : null,
    translations: (message as { translations?: Record<string, string> })
      .translations,
  };
}

function normalizeConversationMembers(
  members:
    | Array<{
        userId: string;
        nickname?: string;
        profileUrl?: string;
        metaData?: { role?: string };
      }>
    | undefined,
): ChatConversationMember[] {
  return (
    members?.map((member) => ({
      id: member.userId,
      name: member.nickname,
      avatarUrl: member.profileUrl,
      systemRoles: normalizeSendbirdRole(member.metaData?.role),
    })) ?? []
  );
}

// @todo - migrate once RBAC migration is complete
function normalizeSendbirdRole(role: string | undefined): string[] | undefined {
  if (role === "MENTEE") return ["mentee"];
  if (role) return ["mentor"];
  return undefined;
}
